/**
 * The payment provider's checkout inside the Billing page (PLAN.md 4.26 P3): the member never leaves
 * vrelis.com, but the card fields are always the provider's own frame. Paddle's script comes from its
 * CDN (Paddle does not allow hosting it); Dodo's SDK is part of our bundle and loaded only here.
 * Both need the CSP exception that `public/_headers` gives to `/billing` only, so a checkout opens
 * only in a document that was loaded at `/billing` (see `billingDocument`).
 */

/** The path the current document was loaded at; the SPA keeps the document, and its CSP, on navigation. */
const DOCUMENT_PATH = typeof location === "undefined" ? "" : location.pathname;
export const billingDocument = () => DOCUMENT_PATH === "/billing";

export type CheckoutSignal = "completed" | "closed" | "error";

/**
 * The provider's checkout frame is one per page, but panels come and go: each open is an attempt with
 * its own number. Only the attempt opened last may route the provider's signals or close the frame,
 * so a panel that went away cannot close the checkout of the panel that replaced it. An attempt whose
 * `signal` was aborted while the script loaded does not open at all.
 */
let attempts = 0;
let current = 0;

/** Pinned by Paddle: v2 of Paddle.js. */
const PADDLE_JS = "https://cdn.paddle.com/paddle/v2/paddle.js";

type PaddleGlobal = {
  Environment: { set(env: "sandbox" | "production"): void };
  Initialize(options: { token: string; eventCallback?: (event: { name?: string }) => void }): void;
  Checkout: { open(options: Record<string, unknown>): void; close(): void };
};

let paddle: Promise<PaddleGlobal> | null = null;
let listener: ((signal: CheckoutSignal) => void) | null = null;

/** Makes this attempt the current one and answers the close of its frame (a no-op once another attempt opened). */
function begin(onSignal: (s: CheckoutSignal) => void, close: () => void): () => void {
  const attempt = ++attempts;
  current = attempt;
  listener = onSignal;
  return () => {
    if (current !== attempt) return;
    current = 0;
    listener = null;
    close();
  };
}

const noop = () => undefined;

function loadPaddle(token: string, environment: string): Promise<PaddleGlobal> {
  paddle ??= new Promise<PaddleGlobal>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = PADDLE_JS;
    script.async = true;
    script.onload = () => {
      const p = (window as unknown as { Paddle?: PaddleGlobal }).Paddle;
      if (!p) return reject(new Error("Paddle.js did not load"));
      if (environment !== "live") p.Environment.set("sandbox");
      p.Initialize({
        token,
        eventCallback: (event) => {
          if (event.name === "checkout.completed") listener?.("completed");
          else if (event.name === "checkout.closed") listener?.("closed");
          else if (event.name === "checkout.error") listener?.("error");
        },
      });
      resolve(p);
    };
    script.onerror = () => reject(new Error("Paddle.js could not be loaded"));
    document.head.appendChild(script);
  }).catch((err: unknown) => {
    paddle = null;
    throw err;
  });
  return paddle;
}

/** Opens Paddle's inline checkout for a transaction the Worker created, in the element with class `frameClass`. */
export async function openPaddle(o: { token: string; environment: string; transactionId: string; email: string | null; frameClass: string; onSignal: (s: CheckoutSignal) => void; signal?: AbortSignal }): Promise<() => void> {
  const p = await loadPaddle(o.token, o.environment);
  if (o.signal?.aborted) return noop;
  const close = begin(o.onSignal, () => p.Checkout.close());
  p.Checkout.open({
    transactionId: o.transactionId,
    ...(o.email ? { customer: { email: o.email } } : {}),
    settings: {
      displayMode: "inline",
      frameTarget: o.frameClass,
      frameInitialHeight: 450,
      frameStyle: "width: 100%; min-width: 312px; background-color: transparent; border: none;",
      variant: "one-page",
    },
  });
  return close;
}

/** Opens Dodo's inline checkout for a checkout session the Worker created, in the element `elementId`. */
export async function openDodo(o: { checkoutUrl: string; environment: string; elementId: string; onSignal: (s: CheckoutSignal) => void; signal?: AbortSignal }): Promise<() => void> {
  const { DodoPayments } = await import("dodopayments-checkout");
  if (o.signal?.aborted) return noop;
  const close = begin(o.onSignal, () => DodoPayments.Checkout.close());
  DodoPayments.Initialize({
    mode: o.environment === "live" ? "live" : "test",
    displayType: "inline",
    onEvent: (event) => {
      if (event.event_type === "checkout.status" && (event.data as { status?: string } | undefined)?.status === "succeeded") listener?.("completed");
      else if (event.event_type === "checkout.closed") listener?.("closed");
      else if (event.event_type === "checkout.error") listener?.("error");
    },
  });
  DodoPayments.Checkout.open({ checkoutUrl: o.checkoutUrl, elementId: o.elementId });
  return close;
}
